import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Edit2, Trash2, Eye, EyeOff, HelpCircle, QrCode } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import EventQuestionsManagement from '../../components/admin/EventQuestionsManagement';

const EventsManagement = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [showQuestionsModal, setShowQuestionsModal] = useState(false);
  const [selectedEventForQuestions, setSelectedEventForQuestions] = useState(null);
  const [showQRCodes, setShowQRCodes] = useState(false);
  const [formData, setFormData] = useState({
    event_name: '',
    event_datefrom: '',
    event_dateto: '',
    is_active: true
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await api.get('/simple.php?action=events');
      setEvents(response.data.data);
    } catch (error) {
      console.error('Error fetching events:', error);
      toast.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (editingEvent) {
        await api.post(`/simple.php?action=events&events_id=${editingEvent.events_id}&_method=PUT`, formData);
        toast.success('Event updated successfully!');
      } else {
        await api.post('/simple.php?action=events', formData);
        toast.success('Event created successfully!');
      }
      
      // Refresh events list
      await fetchEvents();
      
      // Reset form
      setShowForm(false);
      setEditingEvent(null);
      setFormData({
        event_name: '',
        event_datefrom: '',
        event_dateto: '',
        is_active: true
      });
    } catch (error) {
      console.error('Error saving event:', error);
      toast.error(error.response?.data?.message || 'Failed to save event');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      event_name: event.event_name,
      event_datefrom: event.event_datefrom,
      event_dateto: event.event_dateto,
      is_active: event.is_active
    });
    setShowForm(true);
  };

  const handleManageQuestions = (event) => {
    setSelectedEventForQuestions(event);
    setShowQuestionsModal(true);
  };

  const closeQuestionsModal = () => {
    setShowQuestionsModal(false);
    setSelectedEventForQuestions(null);
  };

  const handleDelete = async (eventId, eventName) => {
    if (window.confirm(`Are you sure you want to delete "${eventName}"?`)) {
      try {
        const res = await api.post(`/simple.php?action=events&events_id=${eventId}&_method=DELETE`);
        if (res.data.success) {
          toast.success('Event deleted successfully!');
          fetchEvents();
        } else {
          toast.error(res.data.message || 'Failed to delete event');
        }
      } catch (error) {
        console.error('Error deleting event:', error);
        toast.error(error.response?.data?.message || 'Failed to delete event');
      }
    }
  };

  const toggleStatus = async (event) => {
    try {
      const newActive = event.is_active ? 0 : 1;
      await api.post(`/simple.php?action=events&events_id=${event.events_id}&_method=PUT`, {
        event_name: event.event_name,
        event_datefrom: event.event_datefrom,
        event_dateto: event.event_dateto,
        is_active: newActive
      });
      toast.success(`Event ${newActive ? 'activated' : 'deactivated'} successfully`);
      fetchEvents();
    } catch (error) {
      console.error('Error toggling event status:', error);
      toast.error('Failed to update event status');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getEventEvaluationUrl = (eventId) => {
    // Get the current domain (frontend URL)
    const baseUrl = window.location.origin;
    return `${baseUrl}/evaluation/${eventId}`;
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData({
      event_name: '',
      event_datefrom: '',
      event_dateto: '',
      is_active: true
    });
  };

  if (loading && events.length === 0) {
    return (
      <div className="container" style={{ padding: '40px 20px' }}>
        <div className="loading">Loading events...</div>
      </div>
    );
  }

  return (
    <div className="container" style={{ padding: '40px 20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
        <div>
          <h1 style={{ color: '#1f2937', marginBottom: '8px' }}>Events Management</h1>
          <p style={{ color: '#6b7280', margin: 0 }}>
            Create and manage evaluation events
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button
            onClick={() => setShowQRCodes(!showQRCodes)}
            style={{
              padding: '8px 16px',
              background: showQRCodes ? '#3b82f6' : '#f3f4f6',
              color: showQRCodes ? 'white' : '#374151',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <QrCode size={16} />
            {showQRCodes ? 'Hide QR Codes' : 'Show QR Codes'}
          </button>
          <button 
            className="btn btn-primary"
            onClick={() => setShowForm(true)}
            disabled={loading}
          >
            <Plus size={16} />
            Add New Event
          </button>
        </div>
      </div>

      {/* Add/Edit Event Form */}
      {showForm && (
        <div className="card" style={{ marginBottom: '32px' }}>
          <h3 style={{ marginBottom: '20px' }}>
            {editingEvent ? 'Edit Event' : 'Add New Event'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">Event Name</label>
              <input
                type="text"
                className="form-input"
                value={formData.event_name}
                onChange={(e) => setFormData({ ...formData, event_name: e.target.value })}
                required
                placeholder="Enter event name"
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label className="form-label">Start Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.event_datefrom}
                  onChange={(e) => setFormData({ ...formData, event_datefrom: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">End Date</label>
                <input
                  type="date"
                  className="form-input"
                  value={formData.event_dateto}
                  onChange={(e) => setFormData({ ...formData, event_dateto: e.target.value })}
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                />
                <span className="form-label" style={{ margin: 0 }}>Active (available for evaluation)</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Events List */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '20px' }}>
          <Calendar size={20} style={{ marginRight: '8px' }} />
          <h3 style={{ margin: 0 }}>Events List ({events.length})</h3>
        </div>
        
        {events.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <Calendar size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
            <p>No events found. Create your first event to get started.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Event Name</th>
                  <th>Start Date</th>
                  <th>End Date</th>
                  <th>Status</th>
                  <th>Created</th>
                  {showQRCodes && <th>QR Code</th>}
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.events_id}>
                    <td>
                      <strong>{event.event_name}</strong>
                    </td>
                    <td>{formatDate(event.event_datefrom)}</td>
                    <td>{formatDate(event.event_dateto)}</td>
                    <td>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          background: event.is_active ? '#dcfce7' : '#fef2f2',
                          color: event.is_active ? '#166534' : '#dc2626'
                        }}
                      >
                        {event.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>{formatDate(event.created_at)}</td>
                    {showQRCodes && (
                      <td style={{ textAlign: 'center', padding: '12px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
                          <QRCodeCanvas 
                            value={getEventEvaluationUrl(event.events_id)}
                            size={80}
                            level="M"
                          />
                          <div style={{ 
                            fontSize: '10px', 
                            color: '#6b7280',
                            maxWidth: '120px',
                            wordBreak: 'break-all',
                            textAlign: 'center'
                          }}>
                            {getEventEvaluationUrl(event.events_id)}
                          </div>
                        </div>
                      </td>
                    )}
                    <td>
                      <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-secondary"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => toggleStatus(event)}
                          title={event.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {event.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          className="btn btn-info"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleManageQuestions(event)}
                          title="Manage Questions"
                        >
                          <HelpCircle size={14} />
                        </button>
                        <button
                          className="btn btn-primary"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleEdit(event)}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          className="btn btn-danger"
                          style={{ padding: '6px 8px', fontSize: '12px' }}
                          onClick={() => handleDelete(event.events_id, event.event_name)}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Event Questions Management Modal */}
      {showQuestionsModal && selectedEventForQuestions && (
        <EventQuestionsManagement
          eventId={selectedEventForQuestions.events_id}
          eventName={selectedEventForQuestions.event_name}
          onClose={closeQuestionsModal}
        />
      )}
    </div>
  );
};

export default EventsManagement;